import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, formatMoney, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plane, Ship, Loader2, Lock } from "lucide-react";

const AFRICAN_COUNTRIES = ["Kenya", "Nigeria", "Ghana", "South Africa", "Tanzania", "Uganda", "Rwanda",
  "Egypt", "Morocco", "Ethiopia", "Cameroon", "Senegal", "Ivory Coast", "Zambia", "Zimbabwe"];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("air");
  const [country, setCountry] = useState(user?.country || "Kenya");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");

  if (!user) {
    return (
      <main className="max-w-[600px] mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-extrabold mb-3">Log in to checkout</h1>
        <p className="text-zinc-600 mb-6">Your cart is saved. Sign in to continue.</p>
        <button onClick={() => nav("/login?next=/checkout")} className="btn-primary">Log in</button>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="max-w-[600px] mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-extrabold mb-3">Your cart is empty</h1>
        <button onClick={() => nav("/search")} className="btn-primary">Browse parts</button>
      </main>
    );
  }

  const totalWeight = items.reduce((s, i) => s + (i.quantity || 1) * 5, 0); // rough preview
  const shipping = shippingMethod === "air"
    ? 35 + 8.5 * totalWeight
    : 15 + 2.2 * totalWeight;
  const platformFee = subtotal * 0.05;
  const grandTotal = subtotal + shipping + platformFee;

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!address.trim() || !phone.trim()) {
      toast.error("Please provide delivery address and phone");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map(i => ({ part_id: i.part_id, quantity: i.quantity })),
        shipping_method: shippingMethod,
        delivery_country: country,
        delivery_address: address,
        delivery_phone: phone,
      });
      toast.success("Order placed! Payment holding for 48 hours while we confirm availability.");
      clear();
      nav(`/dashboard?order=${data.order_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-[1200px] mx-auto px-4 lg:px-8 py-12" data-testid="checkout-page">
      <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight mb-8">Checkout</h1>
      <form onSubmit={placeOrder} className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6">
          <section className="bg-white border border-[#E4E4E7] p-6">
            <h2 className="font-display font-extrabold text-xl mb-4 uppercase tracking-tight">Delivery</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Country</label>
                <select value={country} onChange={(e) => setCountry(e.target.value)} className="input-apa" data-testid="checkout-country">
                  {AFRICAN_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="+254 7…" className="input-apa" data-testid="checkout-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Delivery address</label>
                <textarea value={address} onChange={(e) => setAddress(e.target.value)} required rows={3} placeholder="Street, city, landmark…" className="input-apa" data-testid="checkout-address" />
              </div>
            </div>
          </section>

          <section className="bg-white border border-[#E4E4E7] p-6">
            <h2 className="font-display font-extrabold text-xl mb-4 uppercase tracking-tight">Shipping method</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <ShippingOption
                selected={shippingMethod === "air"}
                onClick={() => setShippingMethod("air")}
                icon={<Plane size={22} />}
                title="Air freight"
                subtitle="3–7 days"
                price={35 + 8.5 * totalWeight}
                testid="ship-air"
              />
              <ShippingOption
                selected={shippingMethod === "sea"}
                onClick={() => setShippingMethod("sea")}
                icon={<Ship size={22} />}
                title="Sea freight"
                subtitle="21–45 days"
                price={15 + 2.2 * totalWeight}
                testid="ship-sea"
              />
            </div>
          </section>

          <section className="bg-white border border-[#E4E4E7] p-6">
            <h2 className="font-display font-extrabold text-xl mb-4 uppercase tracking-tight flex items-center gap-2">
              <Lock size={18} /> Payment
            </h2>
            <div className="bg-[#FFD100]/20 border-l-4 border-[#FFD100] p-4 text-sm">
              <b>48-hour availability lock.</b> Your payment is held in escrow. You are only fully charged
              after our sourcing network confirms the exact part. If we can't fulfill, you get a full refund.
            </div>
            <div className="mt-4 text-sm text-zinc-500">
              * Payment gateway integration (Stripe / M-Pesa / Flutterwave) will plug in here.
              For this MVP, checkout is simulated — your order moves straight into the 48-hour hold status.
            </div>
          </section>
        </div>

        <aside className="bg-white border-2 border-[#121212] p-6 h-fit sticky top-24" data-testid="checkout-summary">
          <div className="font-display font-extrabold text-xl mb-4 uppercase tracking-tight">Order</div>
          <div className="space-y-2 mb-4 max-h-[220px] overflow-auto pr-1">
            {items.map(i => (
              <div key={i.part_id} className="flex gap-2 text-sm">
                <div className="w-10 h-10 bg-zinc-100 shrink-0"><img src={i.image_url} alt="" className="w-full h-full object-cover" /></div>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{i.name}</div>
                  <div className="text-xs text-zinc-500">× {i.quantity}</div>
                </div>
                <div className="font-semibold whitespace-nowrap">{formatMoney(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E4E4E7] pt-4 space-y-2 text-sm">
            <Row label="Subtotal" value={formatMoney(subtotal)} />
            <Row label="Shipping" value={formatMoney(shipping)} />
            <Row label="Platform fee" value={formatMoney(platformFee)} />
          </div>
          <div className="border-t border-[#E4E4E7] mt-4 pt-4 flex justify-between items-baseline">
            <div className="font-semibold uppercase text-xs tracking-widest">Total</div>
            <div className="font-display text-3xl font-extrabold">{formatMoney(grandTotal)}</div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full mt-5" data-testid="place-order-button">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            Place order
          </button>
        </aside>
      </form>
    </main>
  );
}

function ShippingOption({ selected, onClick, icon, title, subtitle, price, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-4 border-2 transition-all ${selected ? "border-[#009A44] bg-[#009A44]/5" : "border-[#E4E4E7] hover:border-[#121212]"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className={selected ? "text-[#009A44]" : "text-[#121212]"}>{icon}</div>
        <div>
          <div className="font-display font-extrabold">{title}</div>
          <div className="text-xs text-zinc-500">{subtitle}</div>
        </div>
      </div>
      <div className="text-sm font-semibold">{formatMoney(price)}</div>
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <div className="text-zinc-600">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
